import React, { useMemo, useState, useRef, useEffect } from "react";
import { Plus, Minus, ChevronDown, ChevronUp, Trash2 } from "lucide-react";

// OptionStrat-style option chain. Calls on the LEFT, strike in the MIDDLE,
// puts on the RIGHT. Click any bid (= sell) or ask (= buy) cell to add a
// leg. Greeks columns (delta/IV/volume) are optional and toggle on demand.
//
// Props:
//   chain:        { expirations:[{date,dte}], strikes_by_expiry:{}, quote_table:{} }
//   expiry:       'YYYY-MM-DD' — currently active expiry
//   spot:         current underlying price (used to scroll ATM into view)
//   legs:         existing legs (used to flag already-active rows)
//   onAddLeg:     ({action, type, strike, dte}) => void

const fmt = (v, d = 2) => (Number.isFinite(v) ? Number(v).toFixed(d) : "—");
const fmtVol = (v) => {
  if (!Number.isFinite(v) || v <= 0) return "—";
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
};

const Cell = ({ value, side, kind, strike, action, onClick, dim, active }) => {
  // side='call'/'put'; kind='bid'|'ask'; action='SELL' for bid, 'BUY' for ask.
  if (!Number.isFinite(value)) {
    return <span className="text-white/30 px-2 py-1 inline-block w-full text-right">—</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`forge-chain-${side}-${kind}-${strike}`}
      title={`${action} ${side.toUpperCase()} @ ${strike}`}
      className={`num text-right px-2 py-1 w-full transition rounded-sm ${
        active
          ? action === "BUY"
            ? "bg-[#22C55E]/25 text-[#86EFAC] font-medium"
            : "bg-[#EF4444]/25 text-[#FCA5A5] font-medium"
          : dim
            ? "text-white/40 hover:text-white hover:bg-white/[0.05]"
            : "text-white/85 hover:bg-[#3B82F6]/15 hover:text-white"
      }`}
    >
      {fmt(value)}
    </button>
  );
};

const HeaderCell = ({ children, className = "" }) => (
  <th className={`text-[9.5px] uppercase tracking-[0.18em] text-white/45 font-medium px-2 py-2 ${className}`}>
    {children}
  </th>
);

const OptionChainTable = ({
  chain,
  expiry,
  spot,
  legs = [],
  onAddLeg,
  onClearLegs = null,
  bare = false,
}) => {
  const [showGreeks, setShowGreeks] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const atmRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const expData = chain?.quote_table?.[expiry];
  const strikes = useMemo(
    () => chain?.strikes_by_expiry?.[expiry] || [],
    [chain, expiry],
  );

  // Filter to a tight ±15% band around spot by default (showAll toggles)
  const visibleStrikes = useMemo(() => {
    if (!strikes.length) return [];
    if (showAll || !Number.isFinite(spot)) return strikes;
    const lo = spot * 0.85;
    const hi = spot * 1.15;
    return strikes.filter((s) => s >= lo && s <= hi);
  }, [strikes, spot, showAll]);

  const dteFor = (date) => {
    const exp = chain?.expirations?.find((e) => e.date === date);
    return exp ? exp.dte : 0;
  };

  // Flag a leg as "active" if the user has it on the books
  const legKey = (action, type, strike) =>
    `${action}-${type}-${Number(strike).toFixed(2)}-${dteFor(expiry)}`;
  const activeLegs = useMemo(() => {
    const s = new Set();
    legs.forEach((l) => {
      if (l.type === "stock") return;
      s.add(`${l.action}-${l.type}-${Number(l.strike).toFixed(2)}-${Math.round(l.dte)}`);
    });
    return s;
  }, [legs]);

  // Center the ATM row inside the chain's own scroll container — NOT the
  // whole page. Using element.scrollIntoView() here would also bubble up
  // and scroll the parent /forge route down to the chain on initial mount,
  // which jumps the user past the strategy builder above. We instead
  // compute the scrollTop locally so only the chain's overflow scrolls.
  useEffect(() => {
    const atm = atmRef.current;
    const box = scrollContainerRef.current;
    if (!atm || !box) return;
    const target = atm.offsetTop - box.clientHeight / 2 + atm.clientHeight / 2;
    box.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
  }, [expiry, visibleStrikes.length]);

  if (!chain || !expiry || !expData) {
    return (
      <div
        className={`${bare ? "" : "bg-[#0E1A30] border border-white/10 rounded-xl"} p-6 text-center text-white/45 text-[13px]`}
        data-testid="forge-chain-empty"
      >
        Loading option chain…
      </div>
    );
  }

  const dte = dteFor(expiry);
  const atmStrike = visibleStrikes.reduce(
    (best, s) => (Math.abs(s - spot) < Math.abs(best - spot) ? s : best),
    visibleStrikes[0] || 0,
  );

  const addLeg = (action, type, strike) => {
    onAddLeg && onAddLeg({ action, type, strike, dte });
  };

  return (
    <div
      className={`${bare ? "" : "bg-[#0E1A30] border border-white/10 rounded-xl"} flex flex-col`}
      data-testid="forge-chain-table"
    >
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/60">
          Option Chain · <span className="text-white">{chain.symbol}</span>
          <span className="ml-2 text-white/40">{expiry} · {dte}d</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowGreeks((v) => !v)}
            data-testid="forge-chain-toggle-greeks"
            className="text-[10.5px] uppercase tracking-[0.18em] px-2 py-1 rounded border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition"
          >
            {showGreeks ? "Hide" : "Show"} Δ · IV · Vol
          </button>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            data-testid="forge-chain-toggle-strikes"
            className="text-[10.5px] uppercase tracking-[0.18em] px-2 py-1 rounded border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition inline-flex items-center gap-1"
          >
            {showAll ? (
              <>
                <ChevronUp className="w-3 h-3" /> Tighten
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" /> All strikes
              </>
            )}
          </button>
          {onClearLegs && legs.length > 0 && (
            <button
              type="button"
              onClick={onClearLegs}
              data-testid="forge-chain-clear-legs"
              title="Remove all legs added from this chain"
              className="text-[10.5px] uppercase tracking-[0.18em] px-2 py-1 rounded border border-[#EF4444]/30 text-[#FCA5A5] hover:text-white hover:border-[#EF4444]/60 hover:bg-[#EF4444]/15 transition inline-flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Clear ({legs.length})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div ref={scrollContainerRef} className="overflow-auto max-h-[480px]">
        <table className="w-full border-collapse text-[12px]">
          <thead className="sticky top-0 bg-[#0E1A30] z-10 border-b border-white/10">
            <tr>
              <th colSpan={showGreeks ? 4 : 2} className="text-left">
                <div className="px-4 py-1 text-[10px] uppercase tracking-[0.22em] text-[#22C55E]/80 font-medium">
                  Calls
                </div>
              </th>
              <th className="text-center text-[10px] uppercase tracking-[0.22em] text-white/45 font-medium py-1">
                Strike
              </th>
              <th colSpan={showGreeks ? 4 : 2} className="text-right">
                <div className="px-4 py-1 text-[10px] uppercase tracking-[0.22em] text-[#EF4444]/80 font-medium">
                  Puts
                </div>
              </th>
            </tr>
            <tr className="border-b border-white/10">
              {showGreeks && <HeaderCell className="text-right">Δ</HeaderCell>}
              {showGreeks && <HeaderCell className="text-right">IV</HeaderCell>}
              <HeaderCell className="text-right">Bid <span className="text-white/30 normal-case">sell</span></HeaderCell>
              <HeaderCell className="text-right">Ask <span className="text-white/30 normal-case">buy</span></HeaderCell>
              <HeaderCell className="text-center !text-white/75">—</HeaderCell>
              <HeaderCell className="text-right">Bid <span className="text-white/30 normal-case">sell</span></HeaderCell>
              <HeaderCell className="text-right">Ask <span className="text-white/30 normal-case">buy</span></HeaderCell>
              {showGreeks && <HeaderCell className="text-right">IV</HeaderCell>}
              {showGreeks && <HeaderCell className="text-right">Δ</HeaderCell>}
            </tr>
          </thead>
          <tbody>
            {visibleStrikes.map((strike) => {
              // API returns strike keys as floats like "750.0"; JS number → "750" mismatch.
              const sk = String(strike);
              const skFloat = `${Number(strike).toFixed(1)}`;
              const c = expData.calls?.[sk] || expData.calls?.[skFloat] || expData.calls?.[strike] || {};
              const p = expData.puts?.[sk] || expData.puts?.[skFloat] || expData.puts?.[strike] || {};
              const itm_c = strike < spot;
              const itm_p = strike > spot;
              const isATM = strike === atmStrike;
              return (
                <tr
                  key={strike}
                  ref={isATM ? atmRef : null}
                  className={`border-b border-white/[0.04] ${isATM ? "bg-[#3B82F6]/[0.04]" : "hover:bg-white/[0.02]"}`}
                >
                  {showGreeks && (
                    <td className="px-2 py-0.5 text-right text-white/55 num">{fmt(c.delta, 2)}</td>
                  )}
                  {showGreeks && (
                    <td className="px-2 py-0.5 text-right text-white/55 num">{c.iv ? `${(c.iv * 100).toFixed(0)}%` : "—"}</td>
                  )}
                  <td className="py-0.5">
                    <Cell
                      value={c.bid}
                      side="call"
                      kind="bid"
                      strike={strike}
                      action="SELL"
                      onClick={() => addLeg("SELL", "call", strike)}
                      dim={!itm_c}
                      active={activeLegs.has(legKey("SELL", "call", strike))}
                    />
                  </td>
                  <td className="py-0.5">
                    <Cell
                      value={c.ask}
                      side="call"
                      kind="ask"
                      strike={strike}
                      action="BUY"
                      onClick={() => addLeg("BUY", "call", strike)}
                      dim={!itm_c}
                      active={activeLegs.has(legKey("BUY", "call", strike))}
                    />
                  </td>
                  <td className={`text-center font-medium num px-2 py-0.5 ${
                    isATM ? "text-[#60A5FA]" : "text-white/85"
                  }`}>
                    {Number(strike).toFixed(strike % 1 === 0 ? 0 : 1)}
                  </td>
                  <td className="py-0.5">
                    <Cell
                      value={p.bid}
                      side="put"
                      kind="bid"
                      strike={strike}
                      action="SELL"
                      onClick={() => addLeg("SELL", "put", strike)}
                      dim={!itm_p}
                      active={activeLegs.has(legKey("SELL", "put", strike))}
                    />
                  </td>
                  <td className="py-0.5">
                    <Cell
                      value={p.ask}
                      side="put"
                      kind="ask"
                      strike={strike}
                      action="BUY"
                      onClick={() => addLeg("BUY", "put", strike)}
                      dim={!itm_p}
                      active={activeLegs.has(legKey("BUY", "put", strike))}
                    />
                  </td>
                  {showGreeks && (
                    <td className="px-2 py-0.5 text-right text-white/55 num">{p.iv ? `${(p.iv * 100).toFixed(0)}%` : "—"}</td>
                  )}
                  {showGreeks && (
                    <td className="px-2 py-0.5 text-right text-white/55 num">{fmt(p.delta, 2)}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Hint */}
      <div className="px-4 py-2 border-t border-white/10 text-[10.5px] text-white/40 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1"><Plus className="w-3 h-3 text-[#22C55E]" /> click <b className="text-[#22C55E]">Ask</b> = BUY</span>
        <span className="opacity-40">·</span>
        <span className="inline-flex items-center gap-1"><Minus className="w-3 h-3 text-[#EF4444]" /> click <b className="text-[#EF4444]">Bid</b> = SELL</span>
        <span className="opacity-40">·</span>
        <span>Highlighted row = ATM</span>
      </div>
    </div>
  );
};

export default OptionChainTable;
